import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Optional,
  Renderer2,
  Self,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import { ComponentVariant, Variants } from '../../common/variant/variant';
import { RtInput } from '../../utils/rt-input';

@Component({
  selector: 'rt-checkbox, rt-toggle',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(click)': 'handleClick($event)',
    '(focus)': 'handleFocus($event)',
    '(blur)': 'handleBlur($event)',
    '[class.wrap-label]': 'wrapLabel',
    '[class.is-centered]': 'isCentered',
    '[class.disabled]': 'disabled',
    '[class]': 'variant',
  },
  standalone: true,
  imports: [NgIf, IconColorDirective, IconComponent],
})
export class CheckboxComponent extends RtInput<string | boolean> {
  public static readonly VARIANTS: Variants = ['default', ['default', 'large']];
  @ComponentVariant(CheckboxComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input() public value: string | true = true;
  @Input() public checked: boolean = false;
  @Input() public orangeCheck: boolean = false;
  @Input() public violetCheck: boolean = false;
  @Input() public greyCheck: boolean = false;
  @Input() public direction: 'ltr' | 'rtl' = 'ltr';
  @Input() public fadeOnlyCheckbox: boolean = false;
  @Input() public wrapLabel: boolean = false;
  @Input() public noLabel: boolean = false;
  @Input() public fullWidthLabel: boolean = false;
  @Input() public isCentered: boolean = false;
  @Input() public indeterminateState: boolean = false;
  @Input() public isWeightSemiBold: boolean = false;
  @Input() public hasMargin: boolean = true;
  @Input() public changeOnlyOnIconClick: boolean = false;

  @ViewChild('checkboxIconEl', { static: false }) public checkboxIconEl: ElementRef;
  @ViewChild('toggleIconEl', { static: false }) public toggleIconEl: ElementRef;

  public focused: boolean = false;
  public animated: boolean = false;
  private _mode: string = this.elementRef.nativeElement.tagName.toLowerCase().replace('rt-', '');

  constructor(
    private elementRef: ElementRef,
    private renderer2: Renderer2,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  @Input()
  public set mode(mode: string) {
    this.renderer2.removeClass(this.elementRef.nativeElement, `is-${this._mode}`);
    this._mode = mode;
    this.renderer2.addClass(this.elementRef.nativeElement, `is-${this._mode}`);
  }

  public get mode(): string {
    return this._mode;
  }

  public handleClick(event: MouseEvent): void {
    if (!this.animated) {
      this.setAnimated(true);
    }

    const href = (<SVGURIReference>(<unknown>event.target)).href;

    const isAnchorElement =
      'href' in <HTMLAnchorElement>event.target && !(typeof href === 'object' && 'baseVal' in href);

    if (isAnchorElement) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (this.changeOnlyOnIconClick) {
      if (this.mode === 'checkbox' && !this.checkboxIconEl.nativeElement.contains(event.target)) {
        return;
      }
      if (this.mode === 'toggle' && !this.toggleIconEl.nativeElement.contains(event.target)) {
        return;
      }
    }

    if (!this.disabled && !this.readOnly) {
      this.handleChange(!this.checked ? this.value || true : false);
    }
  }

  public handleBlur(ev: UIEvent): void {
    this.focused = false;
    this.blur.emit(ev);
  }

  public handleFocus(ev: UIEvent): void {
    this.focused = true;
    this.focus.emit(ev);
  }

  protected setValue(value: string | boolean): void {
    this.checkValueType(this.checked, value);
    this.checked = !!value;
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      this.cdRef.detectChanges();
    }
  }

  private setAnimated(value: boolean): void {
    this.animated = value;
    this.cdRef.detectChanges();
  }

  private checkValueType(checked: any, value: any): void {
    if (Array.isArray(checked)) {
      throw new Error(
        '"checked" prop is of a type "array", while it should be of a type "boolean" . This behaviour is deprecated, please change it ASAP!',
      );
    }

    if (Array.isArray(value)) {
      throw new Error(
        '"value" prop is of a type "array", while it should be of a type "string | boolean" . This behaviour is deprecated, please change it ASAP!',
      );
    }
  }
}
